import {
  ElementType,
  forwardRef,
  isValidElement,
  type ReactElement,
  type MouseEventHandler,
  type ReactNode,
} from 'react';

import { Button } from '../../buttons/button/Button.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

/* props of below interface are intentionally unused, to ease the migration to the new AppHeader guideline */
/**
 * @public
 */
export interface AppHeaderActionButtonOwnProps
  extends StylingProps, DataTestId, WithChildren {
  /**
   * Whether the action button is disabled.
   */
  disabled?: boolean;
  /**
   * Whether or not the ActionButton label is shown in the expanded header.
   * If set to false, the label is hidden and only shows in the collapsed menu.
   * If set to false and the prefixIcon is undefined, the label will be shown regardless.
   * @defaultValue true
   */
  showLabel?: boolean;
  /** If set to true, the item will not be moved into the menu in case of insufficient space. */
  pin?: boolean;
  /** Icon shown before the text of the ActionButton. */
  prefixIcon?: ReactElement;
  /** Whether or not the ActionButton is selected. */
  isSelected?: boolean;
  /** Influences the spacings. Condensed is more compact. */
  density?: 'default' | 'condensed';
  /** Handler that is called when the ActionButton is interacted with. */
  onClick?: MouseEventHandler;
}

/**
 * @public
 */
export type AppHeaderActionButtonProps<E extends ElementType> =
  PolymorphicComponentProps<E, AppHeaderActionButtonOwnProps>;

/**
 * Allows to check if a ReactNode is a AppHeaderActionButton element.
 * @internal
 */
export function isAppHeaderActionButton<E extends ElementType>(
  node: ReactNode,
): node is ReactElement<AppHeaderActionButtonProps<E>> {
  return isValidElement(node) && node.type === ActionButton;
}

/**
 * An action button used in `AppHeader.ActionItems`.
 * @public
 */
export const ActionButton: <E extends ElementType = 'button'>(
  props: AppHeaderActionButtonProps<E>,
) => ReactElement | null = forwardRef(
  <E extends ElementType>(
    props: AppHeaderActionButtonProps<E>,
    forwardedRef: typeof props.ref,
  ) => <Button ref={forwardedRef} {...props} />,
);
(ActionButton as typeof ActionButton & { displayName: string }).displayName =
  'AppHeader.Button';
